export type ApiErrorKind =
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "validation"
  | "rate_limited"
  | "server"
  | "http"
  | "network";

export interface ApiErrorInit {
  status: number;
  message: string;
  /** Machine-readable code from the response body (`member_not_found`); a generic one when the body has none. */
  code?: string | undefined;
  /** Extra facts the server attached (`violations`, `module`, `limit`, `allowed`, `license_status`...). */
  details?: Record<string, unknown> | undefined;
  body?: unknown;
  fieldErrors?: Record<string, string>;
  retryAfterSeconds?: number | undefined;
}

/** Any non-2xx response, or a request that never reached the server (status 0). */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  /** Stable, for programs: switch on this, never on `message`. */
  readonly code: string;
  /** Extra facts that came with the error; empty when there were none. */
  readonly details: Record<string, unknown>;
  readonly body: unknown;
  /** Pydantic validation messages by field name (last segment of `loc`). */
  readonly fieldErrors: Record<string, string>;
  readonly retryAfterSeconds: number | undefined;

  constructor(kind: ApiErrorKind, init: ApiErrorInit) {
    super(init.message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = init.status;
    this.code = init.code ?? (kind === "validation" ? "validation_failed" : kind);
    this.details = init.details ?? {};
    this.body = init.body;
    this.fieldErrors = init.fieldErrors ?? {};
    this.retryAfterSeconds = init.retryAfterSeconds;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(init: ApiErrorInit) {
    super("unauthorized", init);
    this.name = "UnauthorizedError";
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isUnauthorized(error: unknown): error is UnauthorizedError {
  return error instanceof UnauthorizedError;
}

/** True for an ApiError carrying one of these codes. */
export function hasCode(error: unknown, ...codes: string[]): error is ApiError {
  return isApiError(error) && codes.includes(error.code);
}

/** One reason a booking broke the club's rules (`rule_violation` errors carry a list of these). */
export interface RuleViolation {
  code: string;
  message: string;
}

export function ruleViolations(error: unknown): RuleViolation[] {
  if (!hasCode(error, "rule_violation")) return [];
  const list = error.details.violations;
  if (!Array.isArray(list)) return [];
  return list.flatMap((item) => {
    const entry = item as { code?: unknown; message?: unknown };
    return typeof entry.message === "string" ? [{ code: typeof entry.code === "string" ? entry.code : "rule", message: entry.message }] : [];
  });
}

/** The codes that mean the license, not the request, is the problem. */
export const LICENSE_READ_ONLY = "license_read_only";
export const MODULE_NOT_LICENSED = "module_not_licensed";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 422) return "validation";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "server";
  return "http";
}

interface ValidationItem {
  loc?: unknown;
  msg?: unknown;
}

interface Parsed {
  message: string | undefined;
  code: string | undefined;
  details: Record<string, unknown>;
  fieldErrors: Record<string, string>;
}

function parseBody(body: unknown): Parsed {
  const result: Parsed = { message: undefined, code: undefined, details: {}, fieldErrors: {} };
  if (typeof body !== "object" || body === null) return result;
  const record = body as { code?: unknown; message?: unknown; detail?: unknown };
  if (typeof record.code === "string") result.code = record.code;
  if (typeof record.message === "string") result.message = record.message;
  const detail = record.detail;
  if (typeof detail === "string") {
    result.message ??= detail;
  } else if (Array.isArray(detail)) {
    // FastAPI request validation: no code, a list of problems.
    const messages: string[] = [];
    for (const item of detail as ValidationItem[]) {
      if (typeof item?.msg !== "string") continue;
      messages.push(item.msg);
      const loc = Array.isArray(item.loc) ? item.loc : [];
      const field = loc.filter((part) => typeof part === "string" && part !== "body" && part !== "query").pop();
      if (typeof field === "string" && !(field in result.fieldErrors)) result.fieldErrors[field] = item.msg;
    }
    result.message ??= messages.join(" ");
  } else if (typeof detail === "object" && detail !== null) {
    const { code, message, ...facts } = detail as Record<string, unknown>;
    if (typeof code === "string") result.code ??= code;
    if (typeof message === "string") result.message ??= message;
    result.details = facts;
  }
  return result;
}

export function errorFromResponse(status: number, statusText: string, body: unknown, headers: Headers): ApiError {
  const { message, code, details, fieldErrors } = parseBody(body);
  const retry = Number(headers.get("Retry-After"));
  const init: ApiErrorInit = {
    status,
    message: message ?? (statusText || `Request failed (${status})`),
    code,
    details,
    body,
    fieldErrors,
    retryAfterSeconds: Number.isFinite(retry) && retry > 0 ? retry : undefined,
  };
  return status === 401 ? new UnauthorizedError(init) : new ApiError(kindFor(status), init);
}

export function networkError(cause: unknown): ApiError {
  const error = new ApiError("network", {
    status: 0,
    message: "Could not reach the server.",
  });
  (error as { cause?: unknown }).cause = cause;
  return error;
}
